// Resend link with an absolute-timestamp countdown.
//
// Derives remaining time from Date.now() vs availableAt rather than
// decrementing a counter (the legacy ActorVerifyEmailStep pattern), so a
// refresh or a backgrounded tab resumes at the right value instead of
// restarting or drifting.

import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import i18next from "i18next";

interface Props {
  availableAt: number;
  onResend: () => void;
  disabled?: boolean;
  labelPrefix?: string;
}

const mmss = (totalSeconds: number) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`;
};

const ResendCountdown: React.FC<Props> = ({
  availableAt,
  onResend,
  disabled,
  labelPrefix,
}) => {
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Math.ceil((availableAt - Date.now()) / 1000))
  );

  useEffect(() => {
    const tick = () =>
      setRemaining(Math.max(0, Math.ceil((availableAt - Date.now()) / 1000)));
    tick();
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [availableAt]);

  const ready = remaining <= 0;

  return (
    <Wrapper>
      {ready ? (
        <ResendButton type="button" onClick={onResend} disabled={disabled}>
          {i18next.t("Resend code")}
        </ResendButton>
      ) : (
        <Waiting aria-live="polite">
          {`${labelPrefix || i18next.t("Resend code in")}: ${mmss(remaining)}`}
        </Waiting>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  text-align: center;
  margin: 18px 0 8px;
  min-height: 20px;
`;

const Waiting = styled.div`
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
`;

const ResendButton = styled.button`
  border: none;
  background: transparent;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  min-height: 44px;

  ${(p) =>
    p.disabled &&
    css`
      color: #8a94a6;
      cursor: default;
    `}
`;

export default ResendCountdown;
